import React, { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import DownloadApp from '@/components/shared/DownloadApp';
import { 
  ShoppingCart, 
  Users, 
  Package, 
  Truck, 
  FileText, 
  DollarSign,
  Smartphone,
  Globe,
  Zap,
  CheckCircle,
  ArrowRight,
  Star,
  Clock,
  TrendingUp
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faMobile, faUser, faStickyNote, faBuilding } from '@fortawesome/free-solid-svg-icons';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: string[];
}

interface PricingOption {
  title: string;
  description: string;
  features: string[];
  cta: string;
  ctaLink: string;
  highlighted?: boolean;
}

const CarPartsSoftwarePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    interest: 'join',
    subject: '',
    message: ''
  });

  const features: Feature[] = [
    {
      icon: <ShoppingCart className="w-12 h-12" />,
      title: "eCommerce Platform",
      description: "Sell car parts online 24/7 with a fully-featured online store",
      benefits: [
        "Mobile-responsive storefront",
        "Secure payment gateway integration",
        "Product catalog with VIN lookup",
        "Shopping cart & checkout optimization"
      ]
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "CRM System",
      description: "Manage customer relationships and drive repeat business",
      benefits: [
        "Customer database management",
        "Purchase history tracking",
        "Automated email marketing",
        "Loyalty program integration"
      ]
    },
    {
      icon: <Package className="w-12 h-12" />,
      title: "Inventory Management",
      description: "Real-time stock control across multiple locations",
      benefits: [
        "Multi-location inventory tracking",
        "Low stock alerts & auto-reordering",
        "Barcode scanning integration",
        "Stock level synchronization"
      ]
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: "Order Management",
      description: "Streamline order processing from quote to delivery",
      benefits: [
        "Automated order processing",
        "Order status tracking",
        "Backorder management",
        "Multi-channel order consolidation"
      ]
    },
    {
      icon: <Truck className="w-12 h-12" />,
      title: "Supply Chain Management",
      description: "Optimize your supply chain with intelligent automation",
      benefits: [
        "Supplier management portal",
        "Purchase order automation",
        "Delivery scheduling & tracking",
        "Returns & warranty processing"
      ]
    },
    {
      icon: <DollarSign className="w-12 h-12" />,
      title: "Invoicing & Quotes",
      description: "Professional invoicing and quotation system",
      benefits: [
        "Instant quote generation",
        "Automated invoicing",
        "Payment tracking & reminders",
        "Tax compliance & reporting"
      ]
    }
  ];

  const pricingOptions: PricingOption[] = [
    {
      title: "Join CarSparesEzyFind",
      description: "List your products on our established marketplace",
      features: [
        "Access to established customer base",
        "iPhone, Android & Huawei apps",
        "Integrated payment processing",
        "Marketing & SEO support",
        "Analytics dashboard",
        "24/7 technical support"
      ],
      cta: "Start Selling Today",
      ctaLink: "#join-platform"
    },
    {
      title: "WhiteLabel Solution",
      description: "Your own branded website & mobile apps",
      features: [
        "Custom branded website",
        "iPhone, Android & Huawei apps",
        "Full eCommerce + CRM + Inventory",
        "Complete control & ownership",
        "48-hour setup guarantee",
        "Dedicated account manager"
      ],
      cta: "Get a Custom Quote",
      ctaLink: "#whitelabel-quote",
      highlighted: true
    }
  ];

  const stats = [
    { value: "10,000+", label: "Car Parts Listed" },
    { value: "500+", label: "Active Sellers" },
    { value: "50,000+", label: "Monthly Orders" },
    { value: "48hrs", label: "WhiteLabel Setup" }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Thank you! We will contact you within 24 hours.');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
       <Head>
        <title>Car Parts Software - eCommerce, CRM, Inventory & More | CarSparesEzyFind</title>
        <meta name="description" content="Complete car parts software solution with eCommerce, CRM, inventory management, ordering, supply chain, invoicing & quotes. Join our platform or get your own WhiteLabel solution in 48 hours." />
        <meta name="keywords" content="car parts software, automotive ecommerce, inventory management, CRM, whitelabel car parts app" />
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 text-white py-20 overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Zap className="w-4 h-4 mr-2 text-yellow-300" />
                <span className="text-sm font-semibold">Complete Automotive Software Solution</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Revolutionize Your <span className="text-yellow-300">Car Parts Business</span>
              </h1>
              
              <p className="text-xl md:text-2xl mb-8 text-blue-100">
                All-in-one software: eCommerce • CRM • Inventory • Orders • Supply Chain • Invoicing
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <a 
                  href="https://www.CarSparesEzyFind.co.za/list-business" 
                  className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center"
                >
                  Join Our Marketplace
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a 
                  href="#whitelabel-quote" 
                  className="bg-white hover:bg-gray-100 text-blue-700 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center border-2 border-white"
                >
                  Get WhiteLabel Solution
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                {stats.map((stat, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-3xl font-bold text-yellow-300">{stat.value}</div>
                    <div className="text-sm text-blue-100 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Everything You Need to Succeed
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our comprehensive software solution includes all the tools to run and grow your car parts business
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-8 hover:shadow-2xl transition-all hover:border-blue-500 transform hover:-translate-y-2"
                >
                  <div className="text-blue-600 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Options Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Choose Your Path to Success
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Whether you want to join our established marketplace or build your own branded platform
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {pricingOptions.map((option, index) => (
                <div 
                  key={index}
                  className={`relative rounded-2xl p-8 ${
                    option.highlighted 
                      ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-2xl transform scale-105' 
                      : 'bg-white border-2 border-gray-200'
                  }`}
                >
                  {option.highlighted && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                        MOST POPULAR
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className={`text-3xl font-bold mb-2 ${option.highlighted ? 'text-white' : 'text-gray-900'}`}>
                      {option.title}
                    </h3>
                    <p className={option.highlighted ? 'text-blue-100' : 'text-gray-600'}>
                      {option.description}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                          option.highlighted ? 'text-yellow-300' : 'text-green-500'
                        }`} />
                        <span className={option.highlighted ? 'text-blue-50' : 'text-gray-700'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <a 
                    href={option.title === "Join CarSparesEzyFind" ? "https://www.CarSparesEzyFind.co.za/list-business" : option.ctaLink}
                    className={`block text-center px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 ${
                      option.highlighted
                        ? 'bg-yellow-400 hover:bg-yellow-300 text-gray-900'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {option.cta}
                    <ArrowRight className="inline-block ml-2 w-5 h-5" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

         {/* Join Platform Section */}
        <section id="join-platform" className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <Globe className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  Join CarSparesEzyFind Marketplace
                </h2>
                <p className="text-xl text-gray-600">
                  Start selling on South Africa's leading car parts platform
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 md:p-12">
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Smartphone className="w-8 h-8 text-blue-600" />
                    </div>
                    <h4 className="font-bold text-lg mb-2">Multi-Platform</h4>
                    <p className="text-gray-600 text-sm">Website + iPhone, Android & Huawei apps</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <TrendingUp className="w-8 h-8 text-green-600" />
                    </div>
                    <h4 className="font-bold text-lg mb-2">Instant Access</h4>
                    <p className="text-gray-600 text-sm">Thousands of ready-to-buy customers</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Clock className="w-8 h-8 text-purple-600" />
                    </div>
                    <h4 className="font-bold text-lg mb-2">Quick Setup</h4>
                    <p className="text-gray-600 text-sm">Get started in minutes, not days</p>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Join?</h3>
                  <ul className="grid md:grid-cols-2 gap-3">
                    {[
                      "No upfront development costs",
                      "Established customer base",
                      "Built-in payment processing",
                      "Professional seller dashboard",
                      "Marketing & SEO included",
                      "Mobile app presence",
                      "24/7 technical support",
                      "Analytics & reporting tools"
                    ].map((benefit, idx) => (
                      <li key={idx} className="flex items-center">
                        <Star className="w-5 h-5 text-yellow-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                  <div className="text-center mt-8">
                  <a 
                    href="https://www.CarSparesEzyFind.co.za/list-business"
                    className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-lg font-bold text-xl transition-all transform hover:scale-105 shadow-xl"
                  >
                    Start Selling Today
                    <ArrowRight className="inline-block ml-2 w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WhiteLabel Section */}
        <section id="whitelabel-quote" className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <Zap className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Get Your Own WhiteLabel Solution
                </h2>
                <p className="text-xl text-blue-100">
                  Your brand, your platform, your success - Setup in just 48 hours!
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <CheckCircle className="w-6 h-6 text-green-400 mr-2" />
                    What You Get
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Fully branded website with your logo & colors",
                      "Native iPhone, Android & Huawei apps",
                      "Complete eCommerce + CRM + Inventory system",
                      "Supply chain & invoicing tools",
                      "Your own domain name",
                      "Unlimited products & users",
                      "Custom features on request",
                      "Full ownership of your platform"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <ArrowRight className="w-5 h-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <Clock className="w-6 h-6 text-blue-400 mr-2" />
                    48-Hour Setup Process
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="bg-blue-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</div>
                      <div>
                        <h4 className="font-bold mb-1">Submit Quote Request</h4>
                        <p className="text-blue-100 text-sm">Fill out the form and tell us about your business</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-blue-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</div>
                      <div>
                        <h4 className="font-bold mb-1">Receive Custom Quote</h4>
                        <p className="text-blue-100 text-sm">Get a detailed proposal within 24 hours</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-blue-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</div>
                      <div>
                        <h4 className="font-bold mb-1">Approve & Launch</h4>
                        <p className="text-blue-100 text-sm">Your branded platform goes live in 48 hours</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-yellow-400/20 border-2 border-yellow-400 rounded-lg">
                    <p className="font-bold text-yellow-300">
                      ⚡ Limited Time: First 10 clients get 20% off setup fees!
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 md:p-12 text-gray-900">
                <h3 className="text-3xl font-bold text-center mb-8">Get Your Custom Quote Now</h3>
                
                <form onSubmit={handleSubmit}>
                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='name' 
                        placeholder='Enter name' 
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-blue-500 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faUser} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='flex flex-col md:flex-row md:gap-5'>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Phone *</label>
                      <div className='relative'>
                        <div className='flex'>
                          <input 
                            type='text' 
                            name='code' 
                            placeholder='27' 
                            value={'+27'} 
                            disabled 
                            className='form-control border border-gray-300 border-r-0 text-sm xl:text-[14px] 2xl:text-md h-10 px-3 w-[50px] rounded-l' 
                          />
                          <input 
                            type='text' 
                            name='phone' 
                            placeholder='Enter mobile number' 
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded-r focus:border-blue-500 focus:outline-none' 
                          />
                        </div>
                        <FontAwesomeIcon icon={faMobile} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Email *</label>
                      <div className='relative'>
                        <input 
                          type='email' 
                          name='email' 
                          placeholder='Enter email Id' 
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-blue-500 focus:outline-none' 
                        />
                        <FontAwesomeIcon icon={faEnvelope} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Business Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='businessName' 
                        placeholder='Enter business name' 
                        value={formData.businessName}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-blue-500 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faBuilding} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>I'm interested in *</label>
                    <select
                      name='interest'
                      value={formData.interest}
                      onChange={handleChange}
                      className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-blue-500 focus:outline-none'
                    >
                      <option value="join">Joining CarSparesEzyFind Marketplace</option>
                      <option value="whitelabel">WhiteLabel Solution</option>
                      <option value="both">Both Options - Need More Info</option>
                    </select>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Subject</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='subject' 
                        placeholder='Subject' 
                        value={formData.subject}
                        onChange={handleChange}
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-blue-500 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faStickyNote} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Message</label>
                    <textarea 
                      name='message'
                      placeholder='Your message' 
                      rows={5} 
                      value={formData.message}
                      onChange={handleChange}
                      className='form-control py-3 border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full px-3 rounded focus:border-blue-500 focus:outline-none'
                    />
                  </div>

                  <div className='text-right'>
                    <button 
                      type="submit"
                      className='bg-[var(--primary-color)] text-white border border-[var(--primary-color)] uppercase transition-all hover:bg-white hover:text-[var(--primary-color)] px-10 py-3 rounded font-bold'
                    >
                      Submit
                    </button>
                  </div>
                </form>

                <p className="text-center text-sm text-gray-600 mt-6">
                  🔒 Your information is secure. We'll contact you within 24 hours.
                </p>
              </div>
            </div>
          </div>
        </section>

          {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Car Parts Business?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Join hundreds of successful car parts businesses already using our platform
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://www.CarSparesEzyFind.co.za/list-business"
                className="bg-white hover:bg-gray-100 text-blue-700 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Join CarSparesEzyFind
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a 
                href="#whitelabel-quote"
                className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Request WhiteLabel Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </section>
      </div>
      <DownloadApp/>
    </>
  );
};

export default CarPartsSoftwarePage;